import { Label } from "@/shadcn/components/ui/label";
// ...existing code...
import { Button } from "@/shadcn/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import React from "react";

type FilterStringProps = {
  filter: string;
  setFilter: (filter: string) => void;
  availableFilters: string[];
  label: string;
};

// Uses a searchable popover dropdown for filter selection, so users can efficiently filter and select from long lists
const FilterString: React.FC<FilterStringProps> = ({
  filter,
  setFilter,
  availableFilters,
  label,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  // Filters strings based on search input, so only relevant options are shown
  const filteredStrings = availableFilters.filter((c) =>
    c.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Handles selection, so the parent state is updated and popover closes
  function handleSelect(value: string) {
    setFilter(value);
    setOpen(false);
    setSearchValue("");
  }

  // Shows the selected filter or a placeholder, so the user always knows the current filter
  const selectedLabel =
    filter === "all"
      ? "All"
      : availableFilters.find((c) => c === filter) || `Filter by ${label}`;

  return (
    <Label>
      {label}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="flex justify-between"
            aria-haspopup="listbox"
          >
            <div className="w-40 truncate text-left">{selectedLabel}</div>
            <ChevronDown className="ml-2 h-3 w-3 text-neutral-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${label}...`}
              value={searchValue}
              onValueChange={setSearchValue}
              disabled={availableFilters.length === 0}
            />
            <CommandList>
              <CommandEmpty>No {label} found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  key="all"
                  value="all"
                  onSelect={() => handleSelect("all")}
                  className="cursor-pointer"
                >
                  All
                  {filter === "all" && (
                    <Check className="neutral-blue-500 ml-auto h-4 w-4" />
                  )}
                </CommandItem>
                {filteredStrings.map((c) => (
                  <CommandItem
                    key={c}
                    value={c}
                    onSelect={() => handleSelect(c)}
                    className={`cursor-pointer ${c === filter ? "" : "pr-5"} `}
                  >
                    {c}
                    {c === filter && (
                      <Check className="neutral-blue-500 ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Label>
  );
};

export default FilterString;
